// Central language metadata for the retailer language-selection feature.
// `name` is the canonical value used everywhere the backend is involved (verify
// response, request header, updateLanguage PUT body) and is ALWAYS lowercase.
// `code` is the short ISO code used internally by i18next.

export interface LanguageOption {
  /** i18next short code, e.g. "en" */
  code: string;
  /** Canonical backend value (lowercase), e.g. "english" — used in header + body */
  name: string;
  /** English label shown in the menu */
  label: string;
  /** Native-script label shown in the menu */
  nativeLabel: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: "en", name: "english", label: "English", nativeLabel: "English" },
  { code: "gu", name: "gujarati", label: "Gujarati", nativeLabel: "ગુજરાતી" },
  { code: "ar", name: "arabic", label: "Arabic", nativeLabel: "العربية" },
  { code: "hi", name: "hindi", label: "Hindi", nativeLabel: "हिन्दी" },
  { code: "bn", name: "bengali", label: "Bengali", nativeLabel: "বাংলা" },
];

export const DEFAULT_LANGUAGE_NAME = "english";

const byName = (name?: string | null) =>
  LANGUAGES.find((l) => l.name === (name || "").toLowerCase());
const byCode = (code?: string | null) =>
  LANGUAGES.find((l) => l.code === (code || "").toLowerCase());

/** Resolve any backend value (e.g. "english") to a known language, falling back to English. */
export const resolveLanguage = (value?: string | null): LanguageOption =>
  byName(value) || byCode(value) || LANGUAGES[0];

/** Map a canonical name ("arabic") to its i18next code ("ar"). */
export const nameToCode = (name?: string | null): string => resolveLanguage(name).code;

/** Map an i18next code ("ar") to its canonical name ("arabic"). */
export const codeToName = (code?: string | null): string => (byCode(code) || LANGUAGES[0]).name;
